(() => {
  const root = document.documentElement;
  const body = document.body;

  if (!body || !body.classList.contains("sa-com-v6")) return;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const revealSelector = "[data-sa-v6-reveal]";
  const depthSelector = "[data-sa-v6-depth]";
  const glassSelector = "[data-sa-v6-glass]";
  let depthItems = [];
  let typeScrollItems = [];
  const sequenceStates = [];
  const lyricStates = [];
  const processStates = [];
  let parallaxFrame = 0;
  const saveData = Boolean(navigator.connection?.saveData);

  const revealAll = (scope = document) => {
    scope.querySelectorAll(revealSelector).forEach((item) => item.classList.add("is-visible"));
  };

  const initReveal = (scope = document) => {
    const items = [...scope.querySelectorAll(revealSelector)].filter((item) => !item.dataset.saV6Observed);

    if (!items.length) return;
    if (reducedMotion.matches || !("IntersectionObserver" in window)) {
      items.forEach((item) => {
        item.dataset.saV6Observed = "true";
        item.classList.add("is-visible");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          currentObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -9% 0px", threshold: 0.12 }
    );

    items.forEach((item) => {
      item.dataset.saV6Observed = "true";
      observer.observe(item);
    });
  };

  const refreshDepthItems = () => {
    depthItems = [...document.querySelectorAll(depthSelector)];
    typeScrollItems = [...document.querySelectorAll("[data-sa-v6-type-scroll]")];
  };

  const paintParallax = () => {
    parallaxFrame = 0;
    if (reducedMotion.matches) {
      depthItems.forEach((item) => item.style.setProperty("--sa-v6-depth-y", "0px"));
      typeScrollItems.forEach((item) => item.style.setProperty("--sa-v6-type-x", "0px"));
      return;
    }

    const viewportCenter = window.innerHeight / 2;
    depthItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.bottom < -180 || rect.top > window.innerHeight + 180) return;
      const strength = Number.parseFloat(item.dataset.saV6Depth || "0.15");
      const distance = rect.top + rect.height / 2 - viewportCenter;
      const offset = Math.max(-110, Math.min(110, distance * strength * -0.18));
      item.style.setProperty("--sa-v6-depth-y", `${offset.toFixed(2)}px`);
    });

    typeScrollItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.bottom < -180 || rect.top > window.innerHeight + 180) return;
      const progress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
      const direction = Number.parseFloat(item.dataset.saV6TypeDirection || "1");
      const travel = Math.min(window.innerWidth * 0.24, 360);
      const offset = (0.5 - progress) * travel * direction;
      item.style.setProperty("--sa-v6-type-x", `${offset.toFixed(2)}px`);
    });

    sequenceStates.forEach((state) => {
      if (!state.near || state.isStatic) return;
      const progressElement = state.progressElement || state.element;
      const rect = progressElement.getBoundingClientRect();
      const hasExternalProgressRoot = progressElement !== state.element;
      const travel = hasExternalProgressRoot
        ? Math.max(window.innerHeight * 0.55, rect.height - window.innerHeight * 0.25)
        : Math.max(1, rect.height - window.innerHeight);
      const progress = hasExternalProgressRoot
        ? Math.max(0, Math.min(1, (window.innerHeight * 0.12 - rect.top) / travel))
        : Math.max(0, Math.min(1, -rect.top / travel));
      const frameIndex = Math.round(progress * (state.urls.length - 1));
      state.showFrame(frameIndex);
    });

    lyricStates.forEach((state) => {
      const rect = state.element.getBoundingClientRect();
      if (rect.bottom < -120 || rect.top > window.innerHeight + 120) return;
      const start = window.innerHeight * 0.84;
      const travel = Math.max(window.innerHeight * 0.54, rect.height * 1.35);
      const progress = Math.max(0, Math.min(1, (start - rect.top) / travel));
      const litWords = Math.round(progress * (state.words.length + 2));
      if (litWords === state.litWords) return;
      state.litWords = litWords;
      state.words.forEach((word, index) => word.classList.toggle("is-lit", index < litWords));
    });

    processStates.forEach((state) => {
      const rootRect = state.element.getBoundingClientRect();
      if (rootRect.bottom < -120 || rootRect.top > window.innerHeight + 120) {
        state.pauseVideos();
        return;
      }

      const centers = state.steps.map((step) => {
        const rect = step.getBoundingClientRect();
        return rect.top + rect.height / 2;
      });
      if (!centers.length) return;

      const anchor = window.innerHeight * (window.innerWidth < 750 ? 0.7 : 0.52);
      let indexFloat = 0;
      const lastIndex = centers.length - 1;

      if (anchor >= centers[lastIndex]) {
        indexFloat = lastIndex;
      } else if (anchor > centers[0]) {
        for (let index = 0; index < lastIndex; index += 1) {
          if (anchor > centers[index + 1]) continue;
          const distance = Math.max(1, centers[index + 1] - centers[index]);
          indexFloat = index + (anchor - centers[index]) / distance;
          break;
        }
      }

      state.render(indexFloat);
    });
  };

  const requestParallax = () => {
    if (parallaxFrame) return;
    parallaxFrame = window.requestAnimationFrame(paintParallax);
  };

  const initGlass = (scope = document) => {
    scope.querySelectorAll(glassSelector).forEach((glass) => {
      if (glass.dataset.saV6GlassReady) return;
      glass.dataset.saV6GlassReady = "true";

      glass.addEventListener(
        "pointermove",
        (event) => {
          if (reducedMotion.matches) return;
          const rect = glass.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;
          glass.style.setProperty("--sa-v6-glass-x", `${x.toFixed(1)}%`);
          glass.style.setProperty("--sa-v6-glass-y", `${y.toFixed(1)}%`);
        },
        { passive: true }
      );
    });
  };

  const initProcess = (scope = document) => {
    scope.querySelectorAll("[data-sa-v6-process]").forEach((process) => {
      if (process.dataset.saV6ProcessReady) return;
      process.dataset.saV6ProcessReady = "true";
      const steps = [...process.querySelectorAll("[data-sa-v6-process-step]")];
      const media = [...process.querySelectorAll("[data-sa-v6-process-media]")];
      const titles = [...process.querySelectorAll("[data-sa-v6-process-title]")];
      if (!steps.length) return;

      const state = {
        element: process,
        steps,
        media,
        titles,
        activeIndex: -1,
        pauseVideos: () => {
          media.forEach((item) => item.querySelector("video")?.pause());
        },
        render: () => {}
      };

      state.render = (indexFloat) => {
        const boundedIndex = Math.max(0, Math.min(steps.length - 1, indexFloat));
        const activeIndex = Math.round(boundedIndex);

        media.forEach((item, mediaIndex) => {
          const distance = Math.abs(boundedIndex - mediaIndex);
          const opacity = Math.max(0, 1 - distance);
          const scale = 1 + Math.min(1, distance) * 0.028;
          item.style.setProperty("--sa-v9-process-opacity", opacity.toFixed(3));
          item.style.setProperty("--sa-v9-process-scale", scale.toFixed(3));
          const isActive = mediaIndex === activeIndex;
          item.classList.toggle("is-active", isActive);
        });

        if (state.activeIndex === activeIndex) return;
        state.activeIndex = activeIndex;
        steps.forEach((step, stepIndex) => step.classList.toggle("is-active", stepIndex === activeIndex));
        titles.forEach((title, titleIndex) => title.classList.toggle("is-active", titleIndex === activeIndex));
        media.forEach((item, mediaIndex) => {
          const video = item.querySelector("video");
          if (!video) return;
          if (mediaIndex === activeIndex && !reducedMotion.matches) {
            const playPromise = video.play();
            playPromise?.catch?.(() => {});
          } else {
            video.pause();
          }
        });
      };

      state.render(0);

      if (reducedMotion.matches) {
        state.pauseVideos();
        return;
      }

      processStates.push(state);
    });
  };

  const initAnchors = (scope = document) => {
    scope.querySelectorAll("[data-sa-v6-anchor]").forEach((anchor) => {
      if (anchor.dataset.saV6AnchorReady) return;
      anchor.dataset.saV6AnchorReady = "true";
      anchor.addEventListener("click", (event) => {
        const hash = anchor.hash;
        if (!hash) return;
        const target = document.querySelector(hash);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion.matches ? "auto" : "smooth", block: "start" });
        window.history.replaceState(null, "", hash);
      });
    });
  };

  const initPointerLight = () => {
    if (!document.querySelector("[data-sa-v6-home]") || reducedMotion.matches) return;
    window.addEventListener(
      "pointermove",
      (event) => {
        root.style.setProperty("--sa-v6-pointer-x", `${event.clientX}px`);
        root.style.setProperty("--sa-v6-pointer-y", `${event.clientY}px`);
      },
      { passive: true }
    );
  };

  const initLyrics = (scope = document) => {
    scope.querySelectorAll("[data-sa-v8-lyrics]").forEach((element) => {
      if (element.dataset.saV8LyricsReady) return;
      element.dataset.saV8LyricsReady = "true";
      const words = element.textContent.trim().split(/\s+/).filter(Boolean);
      if (!words.length) return;
      element.textContent = "";
      const wordElements = words.map((word, index) => {
        const span = document.createElement("span");
        span.className = "sa-v8-lyrics__word";
        span.textContent = word;
        element.append(span);
        if (index < words.length - 1) element.append(document.createTextNode(" "));
        return span;
      });
      const state = { element, words: wordElements, litWords: -1 };
      lyricStates.push(state);
      if (reducedMotion.matches) wordElements.forEach((word) => word.classList.add("is-lit"));
    });
  };

  const initLogoMelt = (scope = document) => {
    scope.querySelectorAll("[data-sa-v8-logo]").forEach((logo) => {
      if (logo.dataset.saV8LogoReady) return;
      logo.dataset.saV8LogoReady = "true";
      const frame = logo.querySelector("[data-sa-v8-logo-frame]");
      const manifest = logo.querySelector("[data-sa-v8-logo-manifest]");
      const header = logo.closest(".header-wrapper");
      if (!frame || !manifest || !header || reducedMotion.matches) return;

      let urls = [];
      try {
        urls = JSON.parse(manifest.textContent);
      } catch (error) {
        return;
      }
      if (!urls.length) return;

      const images = new Map();
      let animationFrame = 0;
      let currentIndex = 0;
      let hasPreloaded = false;
      const lastIndex = urls.length - 1;
      const coarsePointer = window.matchMedia("(hover: none), (pointer: coarse)").matches;

      const preload = () => {
        if (hasPreloaded) return;
        hasPreloaded = true;
        urls.forEach((url, index) => {
          const image = new Image();
          image.decoding = "async";
          image.src = url;
          images.set(index, image);
        });
      };

      const showFrame = (index) => {
        currentIndex = Math.max(0, Math.min(urls.length - 1, index));
        frame.src = urls[currentIndex];
      };

      const animateTo = (targetIndex, fullDuration) => {
        if (targetIndex === currentIndex && !animationFrame) return;
        preload();
        if (animationFrame) window.cancelAnimationFrame(animationFrame);
        animationFrame = 0;

        const fromIndex = currentIndex;
        const distance = Math.abs(targetIndex - fromIndex) / Math.max(1, lastIndex);
        const duration = Math.max(240, fullDuration * distance);
        const isReversing = targetIndex < fromIndex;
        logo.classList.add("is-melting");
        logo.classList.toggle("is-reversing", isReversing);
        logo.classList.remove("is-melted");
        const startedAt = performance.now();

        const tick = (timestamp) => {
          const rawProgress = Math.max(0, Math.min(1, (timestamp - startedAt) / duration));
          const progress = 1 - Math.pow(1 - rawProgress, 3);
          showFrame(Math.round(fromIndex + (targetIndex - fromIndex) * progress));
          if (progress < 1) {
            animationFrame = window.requestAnimationFrame(tick);
            return;
          }

          animationFrame = 0;
          showFrame(targetIndex);
          logo.classList.remove("is-reversing");
          if (targetIndex === 0) {
            logo.classList.remove("is-melting", "is-melted");
          } else if (targetIndex === lastIndex) {
            logo.classList.add("is-melted");
          }
        };

        animationFrame = window.requestAnimationFrame(tick);
      };

      const start = () => animateTo(lastIndex, 3000);
      const restore = () => animateTo(0, 1600);

      header.addEventListener("pointerenter", preload, { passive: true });
      logo.addEventListener("pointerenter", start, { passive: true });
      if (!coarsePointer) header.addEventListener("pointerleave", restore, { passive: true });
      if (coarsePointer || window.innerWidth < 750) window.setTimeout(start, 650);
    });
  };

  const initMeltText = (scope = document) => {
    const items = [...scope.querySelectorAll("[data-sa-v9-melt-text]")].filter(
      (item) => !item.dataset.saV9MeltReady
    );
    if (!items.length) return;

    if (reducedMotion.matches || !("IntersectionObserver" in window)) {
      items.forEach((item) => {
        item.dataset.saV9MeltReady = "true";
        item.classList.add("is-melted");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-melted");
          currentObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -22% 0px", threshold: 0.35 }
    );

    items.forEach((item) => {
      item.dataset.saV9MeltReady = "true";
      observer.observe(item);
    });
  };

  const initSequences = (scope = document) => {
    scope.querySelectorAll("[data-sa-v6-sequence]").forEach((element) => {
      if (element.dataset.saV6SequenceReady) return;
      element.dataset.saV6SequenceReady = "true";

      const canvas = element.querySelector("[data-sa-v6-sequence-canvas]");
      const manifest = element.querySelector("[data-sa-v6-sequence-manifest]");
      if (!canvas || !manifest) return;

      let urls = [];
      try {
        urls = JSON.parse(manifest.textContent);
      } catch (error) {
        element.classList.add("is-static");
        return;
      }

      if (!urls.length) {
        element.classList.add("is-static");
        return;
      }

      const context = canvas.getContext("2d", { alpha: false });
      const state = {
        element,
        progressElement: element.dataset.saV6SequenceProgressRoot
          ? element.closest(element.dataset.saV6SequenceProgressRoot)
          : element,
        canvas,
        context,
        urls,
        images: new Map(),
        currentFrame: -1,
        near: !("IntersectionObserver" in window),
        isStatic: reducedMotion.matches || saveData || !context,
        showFrame: () => {}
      };

      const loadFrame = (frameIndex) => {
        if (frameIndex < 0 || frameIndex >= state.urls.length || state.images.has(frameIndex)) return;
        const image = new Image();
        image.decoding = "async";
        state.images.set(frameIndex, image);
        image.addEventListener(
          "load",
          () => {
            if (state.currentFrame !== frameIndex || !state.context) return;
            state.context.drawImage(image, 0, 0, state.canvas.width, state.canvas.height);
            state.element.classList.add("is-ready");
          },
          { once: true }
        );
        image.src = state.urls[frameIndex];
      };

      state.showFrame = (frameIndex) => {
        if (frameIndex === state.currentFrame) return;
        state.currentFrame = frameIndex;
        state.element.dataset.saV6SequenceFrame = String(frameIndex);

        for (let offset = -3; offset <= 3; offset += 1) loadFrame(frameIndex + offset);

        const currentImage = state.images.get(frameIndex);
        if (currentImage?.complete && currentImage.naturalWidth && state.context) {
          state.context.drawImage(currentImage, 0, 0, state.canvas.width, state.canvas.height);
          state.element.classList.add("is-ready");
        }

        state.images.forEach((image, cachedIndex) => {
          if (Math.abs(cachedIndex - frameIndex) <= 5) return;
          state.images.delete(cachedIndex);
          image.src = "";
        });
      };

      if (state.isStatic) {
        element.classList.add("is-static");
      } else {
        sequenceStates.push(state);
        if ("IntersectionObserver" in window) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                state.near = entry.isIntersecting;
                if (state.near) {
                  state.showFrame(Math.max(0, state.currentFrame));
                  requestParallax();
                }
              });
            },
            { rootMargin: "120% 0px 120% 0px" }
          );
          observer.observe(element);
        }
      }
    });
  };

  const initReviews = (scope = document) => {
    scope.querySelectorAll("[data-sa-v6-reviews]").forEach((carousel) => {
      if (carousel.dataset.saV6ReviewsReady) return;
      carousel.dataset.saV6ReviewsReady = "true";

      const track = carousel.querySelector("[data-sa-v6-reviews-track]");
      const cards = track ? [...track.children] : [];
      const previous = carousel.querySelector("[data-sa-v6-reviews-prev]");
      const next = carousel.querySelector("[data-sa-v6-reviews-next]");
      const status = carousel.querySelector("[data-sa-v6-reviews-status]");
      if (!track || !cards.length || !previous || !next || !status) return;

      let activeIndex = 0;
      const updateStatus = () => {
        status.textContent = `${activeIndex + 1} / ${cards.length}`;
        previous.disabled = activeIndex === 0;
        next.disabled = activeIndex === cards.length - 1;
      };
      const goTo = (nextIndex) => {
        activeIndex = Math.max(0, Math.min(cards.length - 1, nextIndex));
        track.scrollTo({
          left: Math.max(0, cards[activeIndex].offsetLeft - track.offsetLeft - 12),
          behavior: reducedMotion.matches ? "auto" : "smooth",
        });
        updateStatus();
      };

      previous.addEventListener("click", () => goTo(activeIndex - 1));
      next.addEventListener("click", () => goTo(activeIndex + 1));
      track.addEventListener(
        "scroll",
        () => {
          const trackLeft = track.getBoundingClientRect().left;
          activeIndex = cards.reduce((closest, card, index) => {
            const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
            return distance < closest.distance ? { index, distance } : closest;
          }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
          updateStatus();
        },
        { passive: true }
      );
      track.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") goTo(activeIndex - 1);
        if (event.key === "ArrowRight") goTo(activeIndex + 1);
        if (event.key === "Home") goTo(0);
        if (event.key === "End") goTo(cards.length - 1);
      });

      updateStatus();
    });
  };

  const mountReviewSource = () => {
    const mount = document.querySelector("[data-sa-v6-reviews-mount]");
    const source = document.querySelector("[data-sa-v6-reviews-source]");
    if (!mount || !source || source.dataset.saV6ReviewsMounted) return;
    if (window.matchMedia("(max-width: 749px)").matches) return;
    if (!source.children.length && !source.textContent.trim()) return;

    source.dataset.saV6ReviewsMounted = "true";
    source.classList.add("sa-v6-reviews__app", "is-mounted");
    mount.append(source);

    const fallback = mount.parentElement?.querySelector(".sa-v6-reviews__fallback");
    if (fallback) fallback.hidden = true;
  };

  const init = (scope = document) => {
    mountReviewSource();
    initReveal(scope);
    initGlass(scope);
    initLyrics(scope);
    initLogoMelt(scope);
    initMeltText(scope);
    initProcess(scope);
    initAnchors(scope);
    initSequences(scope);
    initReviews(scope);
    refreshDepthItems();
    requestParallax();
  };

  init();
  initPointerLight();

  window.addEventListener("scroll", requestParallax, { passive: true });
  window.addEventListener("resize", requestParallax, { passive: true });

  reducedMotion.addEventListener?.("change", () => {
    if (reducedMotion.matches) revealAll();
    sequenceStates.forEach((state) => {
      state.isStatic = reducedMotion.matches || saveData;
      state.element.classList.toggle("is-static", state.isStatic);
    });
    requestParallax();
  });

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
